// Segment + measure config for the Replenishment view, over the
// `master_replenishment_summary` cube. Every measure is parked at
// (downstream_node, warehouse, channel, item_key, week) and so is valid at every
// segmentation, which is why this file carries no validity matrix.

import type { FilterEntry } from "../../components/ViewControls";
import type { PeriodUnit } from "../../lib/cubePivot";

/** The module this page reports on. Modules are keyed by a per-client numeric
 *  id, so the stable code crosses the wire and the gateway resolves it. */
export const MODULE_CODE = "Replenishment";

export type Units = "eaches" | "sticks" | "cases";

// "sticks" is the cube's column suffix, kept as the value; the label names both,
// because the base unit is a stick for one line and a can for the other.
export const UNIT_OPTIONS: { value: Units; label: string }[] = [
  // An each is one saleable unit of the SKU — a 30-count box, a 4-pack of cans,
  // a single can — so no pack word ("boxes", "cases") is true of all of them.
  { value: "eaches", label: "Eaches" },
  { value: "sticks", label: "Sticks / Cans" },
];

/** The walk's units: its cube also carries each quantity in cases. */
export const WALK_UNIT_OPTIONS: { value: Units; label: string }[] = [
  ...UNIT_OPTIONS,
  { value: "cases", label: "Cases" },
];

/** The segment base units are always split by: a stick and a can are different
 *  things, so a group holding both lines would sum two units into one number. */
export const BASE_UNIT_SPLIT_SEGMENT = "class";

/** The unit mode that needs that split, named once so the two files agree. */
export const BASE_UNIT_MODE: Units = "sticks";

/** How each unit reads in prose. The caption said "sticks" one line under a
 *  control that had stopped calling a can one. */
export const UNIT_LABEL: Record<Units, string> = Object.fromEntries(
  WALK_UNIT_OPTIONS.map((o) => [o.value, o.label.toLowerCase()])
) as Record<Units, string>;

/** Suffix the cube uses for the sticks twin of each quantity measure. */
export const STICKS_SUFFIX = "_sticks";
/** Suffix the walk cube uses for the cases twin of each quantity measure. */
export const CASES_SUFFIX = "_cases";

export interface SegmentOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  disabledReason?: string;
}

/** What a dimension the run's cube never recorded offers instead of grouping. */
export const UNPROFILED_SEGMENT_REASON = "not recorded on this run";

export const SEGMENT_OPTIONS: SegmentOption[] = [
  {
    value: "three_pl",
    label: "3PL",
    description:
      "The logistics partner being replenished. The blank row is everything not " +
      "behind one yet — stock still standing at a co-man, plus any node the run " +
      "left unnamed.",
  },
  {
    value: "warehouse",
    label: "Warehouse",
    description:
      "The site the engine plans at. One partner holds several, and each is " +
      "covered independently — a partner total can read healthy while a site is short.",
    // Segments on the name; the cube is keyed on warehouse_key. Names verified
    // unique per key — segment on the key if that stops holding.
  },
  {
    value: "channel",
    label: "Channel",
    description: "Sales channel of the demand.",
  },
  {
    // Labelled as the Demand page labels it; the value is the engine's column name.
    value: "class",
    label: "Product class",
    description:
      "The line a product belongs to — Drink Mix and Sparkling today. Each is " +
      "built and kitted by its own partners, so this is the coarsest split that " +
      "still plans as one book.",
  },
  {
    value: "item_key",
    label: "SKU",
    description: "The code the engine plans against.",
  },
  {
    value: "sku_name",
    label: "Product",
    description: "The readable name for that code.",
  },
  {
    value: "flavor",
    label: "Flavor",
    description: "Flavor family.",
  },
  {
    value: "pack_count_dim",
    label: "Pack Count",
    description:
      "Pack size for grouping only — not a unit conversion. Items with no pack " +
      "structure group under Unknown.",
  },
  {
    value: "batch_key",
    label: "Batch",
    description:
      "One production run. Its minimum is met across the whole group rather " +
      "than per SKU, so this is the level a run is justified — or refused — at. " +
      "Named by whatever its segment batches on: pack type and flavor, or the " +
      "SKU itself. Blank where nothing batches the product.",
  },
  {
    value: "batch_segment",
    label: "Batch rule set",
    description:
      "Which set of batching rules a product falls under, and so what counts " +
      "as one production run for it. Blank where no segment reaches it — that " +
      "stock is planned with no minimum at all.",
  },
];

/** The options to offer for a run whose cube carries `profiled`.
 *
 *  A run written before a dimension existed keeps a cube without it, and the run
 *  picker offers every completed run — so grouping on one would put the whole
 *  plan in a single blank row beside an identical total. Greyed rather than
 *  dropped: the dimension is a thing this page has, missing on this run.
 *
 *  An EMPTY set is a failed profile read, not a run without dimensions, and
 *  greying the whole control then is worse than offering it. */
export function segmentOptionsFor(
  profiled: ReadonlySet<string>,
  labels: Record<string, string> = SEGMENT_LABEL,
  descriptions: Record<string, string> = {}
): SegmentOption[] {
  const named = SEGMENT_OPTIONS.map((option) =>
    labels[option.value] === option.label && !(option.value in descriptions)
      ? option
      : {
          ...option,
          label: labels[option.value] ?? option.label,
          description: descriptions[option.value] ?? option.description,
        }
  );
  if (profiled.size === 0) return named;
  return named.map((option) =>
    profiled.has(option.value)
      ? option
      : { ...option, disabled: true, disabledReason: UNPROFILED_SEGMENT_REASON }
  );
}

export const SEGMENT_LABEL: Record<string, string> = Object.fromEntries(
  SEGMENT_OPTIONS.map((o) => [o.value, o.label])
);

/** Default grouping — the two lines are built and kitted apart, so they read as
 *  separate books; within each, a partner total hides a short site. Class first
 *  also matches what base units force, so the grouping survives a unit flip. */
export const DEFAULT_SEGMENTS = ["class", "warehouse"];

/** A retired segment and what it became. The default is written into the URL on
 *  mount, so every link minted before a rename still names the old column; without
 *  this it filters out to nothing and the page opens on one whole-plan total. */
export const RETIRED_SEGMENTS: Record<string, string> = { downstream_node: "three_pl" };

/** Hiding keys on the 3PL being BLANK, which is the definition of unallocated
 *  rather than a name a run happens to carry — so it also works on a cube
 *  published before the engine started labelling the bucket. */
export const UNALLOCATED_COLUMN = "three_pl";

/** Kept as a word rather than a boolean: it rides in the URL beside the other
 *  view params, and "hide" reads in a shared link where "false" does not. */
export const UNALLOCATED_MODES = ["show", "hide"] as const;
export type UnallocatedMode = (typeof UNALLOCATED_MODES)[number];

export const UNALLOCATED_LABEL = "Show unallocated";

/** What the filter control calls "no value". Blanks are a real group on this
 *  page — stock not behind a 3PL yet — so they have to be selectable, and a
 *  dropdown cannot carry a null. The read maps this back to IS NULL, so it must
 *  match apply_ui_filters.BLANK_FILTER_VALUE. */
export const BLANK_FILTER_VALUE = "(blank)";

/** The grouping and the one to fall back to when a run predates the 3PL stamp,
 *  so an older run opens on a breakdown rather than one blank row. */
export const THREE_PL_SEGMENT = "three_pl";
export const FALLBACK_SEGMENT = "warehouse";

/** Opens on the position — the question the tab exists to answer. */
export const DEFAULT_MEASURE = "ending_inventory";

// ── Measures ────────────────────────────────────────────────────────────────

/** The engine's own walk lines in reading order, renamed to snake_case once in
 *  logic_replenishment_walk.WALK_LINES and carried unchanged from there. */
export const QUANTITY_MEASURES = [
  "starting_inventory",
  "open_transfer_arrivals",
  "forecasted_transfer_arrivals",
  "build_plan_arrivals",
  "recommended_arrivals",
  "order_backlog",
  "forecasted_demand",
  "unfulfilled_demand",
  "ending_inventory",
  "change_in_inventory",
  "order_rate",
  // Not a walk line — joined onto the cube from the engine's own ledger — but a
  // quantity in the same unit, so it carries a sticks twin like the rest.
  "pre_constraint_build_qty",
  // The First Pack walk's two halves of `forecasted_demand`; absent from the
  // Finished Goods cube.
  "direct_demand",
  "repack_demand",
  // The Repack Site walk's committed part of its demand line: the lock's split.
  "committed_demand",
  // The Repack Site walk's information line: released for a repack, not yet repacked.
  "repack_in_progress",
  // The Finished Goods walk's information line, every build by build week, and its
  // committed part, which the line's lock reads.
  "planned_builds",
  "committed_builds",
] as const;

/** Ratios, which carry no sticks twin. Cover is computed from the eaches columns
 *  in both unit modes: for one item the unit cancels, but across items of
 *  different pack sizes it does not, so an aggregate reads the eaches answer. */
export const RATIO_MEASURES = ["ending_weeks_of_supply"] as const;

/** Coverage walks the ending inventory forward through the actual forecast, so
 *  it needs the whole demand series at the grouping on screen and is recomputed
 *  after every rollup. Must stay in step with `coverage.with_coverage` in steps. */
export const COVERAGE_INPUTS: Record<string, { inventory: string; demand: string[] }> = {
  ending_weeks_of_supply: {
    inventory: "ending_inventory",
    demand: ["order_backlog", "forecasted_demand"],
  },
};

/** A rate, not a quantity, so it carries no sticks twin. */
export const RATE_MEASURE = "order_rate";

/** How each measure reads in the walk equation, top to bottom:
 *
 *      Starting Inventory
 *    + Open Transfer Arrivals
 *    + Planned Build Arrivals
 *    - Forecasted Demand
 *    = Ending Inventory
 *
 *  The engine's own identity, asserted in the walk's tests rather than here. */
export type MeasureRole = "open" | "add" | "subtract" | "close" | "ratio" | "reference";

export const MEASURE_ROLE: Record<string, MeasureRole> = {
  target_weeks_of_supply: "reference",
  starting_inventory: "open",
  open_transfer_arrivals: "add",
  // The term is the parent; its two halves are the breakdown the lock reads.
  forecasted_transfer_arrivals: "add",
  build_plan_arrivals: "reference",
  recommended_arrivals: "reference",
  order_backlog: "subtract",
  forecasted_demand: "subtract",
  // Plain, not red: this is usually demand that is late rather than lost, since
  // the engine carries it and serves it when the next arrival lands.
  unfulfilled_demand: "reference",
  ending_inventory: "close",
  ending_weeks_of_supply: "ratio",
  change_in_inventory: "reference",
  order_rate: "reference",
  // Not a term in the walk: this and Recommended Arrivals are the same supply
  // counted before and after the minimum, so adding both would double it.
  pre_constraint_build_qty: "reference",
};

export const ROLE_PREFIX: Record<MeasureRole, string> = {
  open: "",
  add: "+",
  subtract: "\u2212",
  close: "=",
  ratio: "",
  reference: "",
};

export const WALK_MEASURES: string[] = [
  "starting_inventory",
  "open_transfer_arrivals",
  // The engine's one `future_supply` line, undivided — the split rides the lock.
  "forecasted_transfer_arrivals",
  // `order_backlog` is parked like `order_rate` below: the position it was
  // subtracted from is already net of it, so it came off twice.
  // The engine still books it, and the page shows the engine's own
  // `ending_inventory`, so week one reads short of its rows by that amount.
  "forecasted_demand",
  "ending_inventory",
];

/** Highlighted band directly under the walk — the number the page exists for. */
export const BAND_MEASURES: string[] = ["ending_weeks_of_supply"];

export const REFERENCE_MEASURES: string[] = [
  // Read against Recommended Arrivals: ordering more than this is a minimum
  // rounding the order up, ordering nothing is a run declined as too small.
  // Both roll up as `flow` (no ROLLUP_KIND entry); giving either one a kind
  // without the other breaks that comparison at month and above.
  "pre_constraint_build_qty",
  // Context, not a term in the walk, so the equation above does not close in a
  // week that went short — by exactly this much.
  "unfulfilled_demand",
  // The two halves of Planned Build Arrivals are parked like `order_rate` below: the walk
  // carries their sum, and the lock — which reads them off the cube — says how
  // much of it is committed.
  "change_in_inventory",
  // `order_rate` is parked, not deleted: the cube still carries it and its
  // label, role and rollup kind are all still here, so putting it back on the
  // page is adding the one string. It came off because nothing on this page
  // divides by it — cover is a forward run-out, not `inventory / rate` — so it
  // read as the denominator of a number it has nothing to do with.
];

export const REFERENCE_LABEL = "REFERENCE";

export const MEASURE_ORDER: string[] = [...WALK_MEASURES, ...BAND_MEASURES, ...REFERENCE_MEASURES];

/** The fill sites' plan per Manufacturing SKU, in the week it lands at the 3PL. */
export const FILL_BUILDS_MEASURE = "fill_builds";

/** The committed builds in the week they are built, and the site building them. */
export const COMMITTED_BUILDS_MEASURE = "committed_builds";
/** Every build in the week it is built, recommended included; committed_builds is its firm part. */
export const PLANNED_BUILDS_MEASURE = "planned_builds";
export const BUILD_FACILITY_COL = "build_facility";

// Planner-facing wording; the mapping to the engine's own measure ids lives in
// logic_replenishment_walk.WALK_LINES.
export const MEASURE_LABEL: Record<string, string> = {
  starting_inventory: "Starting Inventory",
  open_transfer_arrivals: "Open Transfer Arrivals",
  build_plan_arrivals: "Build Plan Arrivals",
  recommended_arrivals: "Recommended Arrivals",
  forecasted_transfer_arrivals: "Planned Build Arrivals",
  [COMMITTED_BUILDS_MEASURE]: "Committed Builds",
  [PLANNED_BUILDS_MEASURE]: "Planned Builds",
  order_backlog: "Order Backlog",
  forecasted_demand: "Forecasted Demand",
  unfulfilled_demand: "Unfulfilled Demand",
  ending_inventory: "Ending Inventory",
  change_in_inventory: "Change in Inventory",
  order_rate: "Order Rate",
  pre_constraint_build_qty: "Needed Before Minimums",
  ending_weeks_of_supply: "Ending Weeks of Supply",
  target_weeks_of_supply: "Target Weeks of Supply",
  direct_demand: "Direct Demand",
  repack_demand: "Repack Demand",
  committed_demand: "Committed Demand",
  repack_in_progress: "In Transit / Being Repacked",
};

export const MEASURE_DESCRIPTION: Record<string, string> = {
  starting_inventory: "Stock on hand at the start of the period.",
  open_transfer_arrivals:
    "Stock that already exists — on hand at the co-man or in transit — arriving this period.",
  build_plan_arrivals: "Build plan arriving this period. Already committed.",
  recommended_arrivals:
    "Supply Atomic recommends ordering to cover demand the build plan does not reach.",
  [FILL_BUILDS_MEASURE]:
    "What the fill site's plan lands at the 3PLs, in the week it lands: the committed plan " +
    "plus what Atomic recommends, direct and through repack together. Lock a week to commit it.",
  [COMMITTED_BUILDS_MEASURE]:
    "Committed builds — purchase orders and locked weeks — in the week they are built, " +
    "ahead of the week they arrive. Hover a cell for the facility building it.",
  [PLANNED_BUILDS_MEASURE]:
    "Every build in the week it is built, ahead of the week it arrives: committed builds " +
    "(purchase orders and locked weeks) plus what Atomic recommends. A lock marks the " +
    "committed part. Hover a cell for the facility building it.",
  forecasted_transfer_arrivals:
    "The committed build plan plus the supply Atomic recommends, in the period it lands. " +
    "A solid lock means the period is fully committed, a faint one partly.",
  order_backlog: "Orders still owed to customers.",
  forecasted_demand: "The sales forecast for this period.",
  unfulfilled_demand:
    "Demand this period could not serve. A warehouse cannot hold negative " +
    "stock, so the position stops at zero and the shortfall is named here " +
    "instead of being sunk into it. Usually late rather than lost — the " +
    "engine carries it and serves it when the next arrival lands.",
  ending_inventory:
    "Starting inventory plus arrivals, less demand — floored at zero, since a " +
    "warehouse cannot hold a negative. Where it would have gone below, the " +
    "shortfall is on Unfulfilled Demand.",
  change_in_inventory: "Ending inventory less starting inventory, both floored.",
  order_rate: "Average weekly demand over this period and the next three.",
  pre_constraint_build_qty:
    "What covering demand needed this period, before any minimum order " +
    "quantity was applied. If more than this arrives, a minimum rounded the " +
    "order up. If nothing arrives while this shows a real number, the run " +
    "was too small to be worth building and was declined rather than " +
    "overstocked.",
  ending_weeks_of_supply:
    "How many weeks the ending inventory covers, against the forecast ahead of it.",
  target_weeks_of_supply:
    "The cover this run planned to, from its own settings. Weeks below it are shaded.",
  direct_demand: "The forecast customers take in this item as it is filled.",
  repack_demand: "The forecast repack consumes from this item to build another.",
  committed_demand: "The part of the demand line committed POs account for.",
  repack_in_progress:
    "Cans released from the fill for a repack here and not yet repacked. Not counted in the site's stock.",
};

// ── Stages ──────────────────────────────────────────────────────────────────

/** The two production stages the walk can be stated at — two values of
 *  `output_production_stage` (docs/production-stage-normalization.md). A
 *  walk-level setting, never a segment: the two are different numbers for the
 *  same cans, so they are never offered side by side as rows of one walk. */
export type Stage = "finished_good" | "first_pack" | "repack_site";

export const FIRST_PACK_STAGE: Stage = "first_pack";
export const FINISHED_GOOD_STAGE: Stage = "finished_good";
/** The walk at the repack sites: the filled item they hold, receive and repack. */
export const REPACK_SITE_STAGE: Stage = "repack_site";

/** The Finished Goods view opens by default. */
export const DEFAULT_STAGE: Stage = FINISHED_GOOD_STAGE;

export const STAGE_LABEL: Record<Stage, string> = {
  finished_good: "Finished Goods",
  first_pack: "First Pack",
  repack_site: "Repack Site",
};

/** In the order the goods move: filled, repacked, stocked. */
export const STAGE_OPTIONS: { value: Stage; label: string }[] = [
  { value: FIRST_PACK_STAGE, label: STAGE_LABEL.first_pack },
  { value: REPACK_SITE_STAGE, label: STAGE_LABEL.repack_site },
  { value: FINISHED_GOOD_STAGE, label: STAGE_LABEL.finished_good },
];

export function isStage(value: unknown): value is Stage {
  return value === FIRST_PACK_STAGE || value === FINISHED_GOOD_STAGE || value === REPACK_SITE_STAGE;
}

export const ALL_STAGES: readonly Stage[] = STAGE_OPTIONS.map((o) => o.value);

/** The stage a walk shows for `asked`: that stage where this walk offers it, else
 *  the default where offered, else the walk's first. */
export function openingStage(asked: unknown, stages: readonly Stage[] = ALL_STAGES): Stage {
  if (isStage(asked) && stages.includes(asked)) return asked;
  return stages.includes(DEFAULT_STAGE) ? DEFAULT_STAGE : (stages[0] ?? DEFAULT_STAGE);
}

/** What the SKU segment IS at each stage: the First Pack walk's rows are the
 *  MFG SKU the co-man fills, the Finished Goods walk's the FG SKU the customer
 *  buys. The column is `item_key` on both cubes; only its meaning moves. */
export const SKU_LABEL: Record<Stage, string> = {
  first_pack: "SKU (MFG)",
  finished_good: "SKU (FG)",
  repack_site: "SKU (MFG)",
};

/** The segment labels at `stage`: SEGMENT_LABEL with the SKU named for its
 *  walk. Undefined — a page with the one walk it always had — keeps "SKU". */
/** On First Pack the warehouse is the goods' final destination, named so. */
export const CUSTOMER_WAREHOUSE_LABEL = "Customer Warehouse";

/** What the warehouse column is at each walk: the 3PL on Finished Goods, where the
 *  goods end up on First Pack, the site's own location on the repack-site walk. */
const WAREHOUSE_LABEL: Record<Stage, string> = {
  finished_good: "Warehouse",
  first_pack: CUSTOMER_WAREHOUSE_LABEL,
  repack_site: "Location",
};

const FINAL_DESTINATION_DESCRIPTION: Record<string, string> = {
  warehouse:
    "The final destination: the warehouse whose customer demand the goods served. " +
    "Each is covered independently, so a partner total can read healthy while one is short.",
};

/** The segment descriptions at `stage`: the warehouse is a customer's only where the
 *  walk stands at the 3PLs, never at a repack site. */
export function segmentDescriptionsFor(stage: Stage | undefined): Record<string, string> | undefined {
  return stage && stage !== REPACK_SITE_STAGE ? FINAL_DESTINATION_DESCRIPTION : undefined;
}

export function segmentLabelsFor(stage: Stage | undefined): Record<string, string> {
  return stage
    ? { ...SEGMENT_LABEL, warehouse: WAREHOUSE_LABEL[stage], item_key: SKU_LABEL[stage] }
    : SEGMENT_LABEL;
}

/** Every stage but `stage` this walk offers, for reading ahead of a flip. */
export function otherStages(stage: Stage, stages: readonly Stage[] = ALL_STAGES): Stage[] {
  return stages.filter((s) => s !== stage);
}

/** The demand term and, on the First Pack walk, its two halves. */
export const DEMAND_MEASURE = "forecasted_demand";
export const DIRECT_DEMAND_MEASURE = "direct_demand";
export const REPACK_DEMAND_MEASURE = "repack_demand";

/** Child line → the parent it sits beneath. The parent is the group's one
 *  demand number; the children say how much of it repack is consuming. */
export const FIRST_PACK_MEASURE_PARENT: Record<string, string> = {
  [DIRECT_DEMAND_MEASURE]: DEMAND_MEASURE,
  [REPACK_DEMAND_MEASURE]: DEMAND_MEASURE,
};

/** The walk lines the First Pack walk shows, in reading order: the demand
 *  parent with its two halves spliced directly beneath it, exactly where the
 *  Finished Goods walk shows the one demand line. */
export const FIRST_PACK_WALK_MEASURES: string[] = [
  "starting_inventory",
  "open_transfer_arrivals",
  "forecasted_transfer_arrivals",
  DEMAND_MEASURE,
  DIRECT_DEMAND_MEASURE,
  REPACK_DEMAND_MEASURE,
  "ending_inventory",
];

/** The First Pack roles: the demand parent is a reference line — the same
 *  role the supply parent's halves carry — and its two halves are the terms
 *  that subtract, so the equation still closes once:
 *
 *      Starting Inventory
 *    + Open Transfer Arrivals
 *    + Planned Build Arrivals
 *      Forecasted Demand
 *    - Direct Demand
 *    - Repack Demand
 *    = Ending Inventory
 */
export const FIRST_PACK_MEASURE_ROLE: Record<string, MeasureRole> = {
  ...MEASURE_ROLE,
  [DEMAND_MEASURE]: "reference",
  [DIRECT_DEMAND_MEASURE]: "subtract",
  [REPACK_DEMAND_MEASURE]: "subtract",
};

/** What the walk shows at one stage. A measure reads the same wherever it appears
 *  unless a stage names or describes it for itself. */
export interface WalkSpec {
  stage: Stage;
  walkMeasures: string[];
  bandMeasures: string[];
  referenceMeasures: string[];
  /** Every measure the Aggregate by picker offers, in page order. */
  measureOrder: string[];
  role: Record<string, MeasureRole>;
  /** Child → parent, for the lines nested beneath a parent line. */
  parent: Record<string, string>;
  /** Line names that differ at this stage; the rest read as MEASURE_LABEL. */
  labels?: Record<string, string>;
  /** Line hovers that differ at this stage; the rest read as MEASURE_DESCRIPTION. */
  descriptions?: Record<string, string>;
  /** The lines that carry the lock, and the measure holding their committed part. */
  locks: { measure: string; firm: string; recommended?: string; committedWord: string }[];
  /** A line broken down by a cube column, read by grouping on it: as lines beneath
   *  it (`firm` locks each part), or named in the line's hover. */
  breakdown?: { measure: string; column: string; firm?: string; show?: "lines" | "hover" };
  /** The grouping the walk opens on. */
  defaultSegments: string[];
  /** The fill sites' plan, edited in ARRIVAL weeks beneath the `under` line it is part of. */
  buildsLine?: { measure: string; under: string };
  /** The one unit the walk reads in, shown in place of the Units choice. */
  units?: { value: Units; label: string };
  /** A shortfall line drawn in red: the planner has to act on it. */
  alert?: string;
  /** Label block width (px) for a walk whose line names outrun its row labels. */
  labelWidth?: number;
}

/** Released for a repack at the site and not yet repacked: an information line. */
export const REPACK_IN_PROGRESS_MEASURE = "repack_in_progress";

/** The Repack Site walk's breakdown column: the finished good a repack line becomes. */
export const REPACK_INTO_COL = "repacks_into";

/** Label widths (px) for segments whose values outrun the grid's 280px fit cap. */
export const SEGMENT_LABEL_WIDTHS: Readonly<Record<string, number>> = { sku_name: 480 };

/** Detail-row keys of a breakdown part: the prefix, then the part's value. */
export const BREAKDOWN_KEY_PREFIX = "breakdown:";

/** Planned Build Arrivals: the build plan is its committed part. */
const ARRIVALS_LOCK = {
  measure: "forecasted_transfer_arrivals",
  firm: "build_plan_arrivals",
  recommended: "recommended_arrivals",
  committedWord: "Build plan",
};

/** A line's name at `spec`'s stage. */
export function measureLabel(spec: WalkSpec, measure: string): string {
  return spec.labels?.[measure] ?? MEASURE_LABEL[measure] ?? measure;
}

/** A line's hover at `spec`'s stage. */
export function measureDescription(spec: WalkSpec, measure: string): string | undefined {
  return spec.descriptions?.[measure] ?? MEASURE_DESCRIPTION[measure];
}

export const WALK_SPEC: Record<Stage, WalkSpec> = {
  finished_good: {
    stage: FINISHED_GOOD_STAGE,
    walkMeasures: WALK_MEASURES,
    bandMeasures: BAND_MEASURES,
    referenceMeasures: [PLANNED_BUILDS_MEASURE, ...REFERENCE_MEASURES],
    measureOrder: [...MEASURE_ORDER, PLANNED_BUILDS_MEASURE],
    role: { ...MEASURE_ROLE, [PLANNED_BUILDS_MEASURE]: "reference" },
    parent: {},
    locks: [ARRIVALS_LOCK, { measure: PLANNED_BUILDS_MEASURE, firm: COMMITTED_BUILDS_MEASURE, committedWord: "Build plan" }],
    breakdown: { measure: PLANNED_BUILDS_MEASURE, column: BUILD_FACILITY_COL, show: "hover" },
    defaultSegments: DEFAULT_SEGMENTS,
  },
  first_pack: {
    stage: FIRST_PACK_STAGE,
    walkMeasures: FIRST_PACK_WALK_MEASURES,
    bandMeasures: BAND_MEASURES,
    referenceMeasures: REFERENCE_MEASURES,
    measureOrder: [...FIRST_PACK_WALK_MEASURES, ...BAND_MEASURES, ...REFERENCE_MEASURES],
    role: FIRST_PACK_MEASURE_ROLE,
    parent: FIRST_PACK_MEASURE_PARENT,
    // The repack a planner signed at the site (a repack PO's cans) is committed.
    locks: [
      ARRIVALS_LOCK,
      { measure: REPACK_DEMAND_MEASURE, firm: "committed_demand", committedWord: "Repack PO" },
    ],
    // One row per manufacturing SKU, its demand split beneath: the build plan the
    // co-man fills. Where the goods end up is a split to add, not the default.
    defaultSegments: ["class", "item_key"],
    labels: {
      [FILL_BUILDS_MEASURE]: "Planned Build Arrivals – from DrinkPak",
    },
    buildsLine: { measure: FILL_BUILDS_MEASURE, under: "forecasted_transfer_arrivals" },
  },
  // Stock at the site in the filled item: fill in, repack out. No cover target
  // is set for a site, so the cover band stays off.
  repack_site: {
    stage: REPACK_SITE_STAGE,
    walkMeasures: WALK_MEASURES,
    bandMeasures: [],
    // An information line, outside the walk's balance.
    referenceMeasures: [REPACK_IN_PROGRESS_MEASURE, ...REFERENCE_MEASURES],
    measureOrder: [...WALK_MEASURES, REPACK_IN_PROGRESS_MEASURE, ...REFERENCE_MEASURES],
    role: { ...MEASURE_ROLE, [REPACK_IN_PROGRESS_MEASURE]: "reference" },
    parent: {},
    labels: {
      starting_inventory: "Starting Stock at Site",
      open_transfer_arrivals: "In Transit from Fill",
      forecasted_transfer_arrivals: "Planned from Fill",
      forecasted_demand: "Planned Repack Production",
      ending_inventory: "Ending Stock at Site",
      // A repack the site cannot feed, not a customer order served late.
      unfulfilled_demand: "Insufficient Inventory for Build Plan",
    },
    descriptions: {
      unfulfilled_demand:
        "Cans a repack PO here needs this period that neither the site's stock nor fill " +
        "transfers landing in time cover. The PO still counts in full where its finished " +
        "good is sold, so that stock reads high by what these cans would have made.",
      ending_inventory:
        "Starting stock plus arrivals, less planned repack production — floored at zero. " +
        "Where it would have gone below, the shortfall is on Insufficient Inventory for Build Plan.",
      change_in_inventory: "Ending stock at the site less starting stock, both floored.",
    },
    locks: [
      { ...ARRIVALS_LOCK, committedWord: "Fill PO" },
      { measure: "forecasted_demand", firm: "committed_demand", committedWord: "Repack PO" },
    ],
    breakdown: { measure: "forecasted_demand", column: REPACK_INTO_COL, firm: "committed_demand" },
    defaultSegments: DEFAULT_SEGMENTS,
    // The site holds loose filled cans, not saleable packs, so eaches mean nothing here.
    // Only the cans line repacks, so this walk never holds sticks, even unlocked.
    units: { value: BASE_UNIT_MODE, label: "Cans" },
    alert: "unfulfilled_demand",
    // Its lines name the finished goods each repack makes, longer than a site's name.
    labelWidth: 480,
  },
};

/** The grouping after a flip from `from` to `to`: one still at the old walk's
 *  default takes the new walk's; one the viewer chose is kept as it is. */
export function segmentsAfterFlip(
  segments: readonly string[],
  from: Stage,
  to: Stage,
  defaults: (stage: Stage) => string[] = (stage) => WALK_SPEC[stage].defaultSegments
): string[] {
  const old = defaults(from);
  const untouched = segments.length === old.length && segments.every((s, i) => s === old[i]);
  return untouched ? defaults(to) : [...segments];
}

/** The Aggregate by measure to show at `stage`, given the one chosen.
 *
 *  A measure both walks carry is kept. The demand line maps to its direct half
 *  on the way to First Pack and either half maps back to the demand line on
 *  the way to Finished Goods, so a flip lands on the same question rather
 *  than on a measure the target walk does not have. Anything else the target
 *  lacks falls to the page default. */
export function sortByForStage(sortBy: string, stage: Stage): string {
  const spec = WALK_SPEC[stage];
  if (stage === FIRST_PACK_STAGE && sortBy === DEMAND_MEASURE) return DIRECT_DEMAND_MEASURE;
  if (spec.measureOrder.includes(sortBy)) return sortBy;
  if (sortBy === DIRECT_DEMAND_MEASURE || sortBy === REPACK_DEMAND_MEASURE) return DEMAND_MEASURE;
  return DEFAULT_MEASURE;
}

/** The cube key for a measure at the chosen units. Ratios and rates ignore
 *  units — a quotient is unit-invariant, and a rate has no sticks twin. */
export function measureKey(measure: string, units: Units): string {
  if ((RATIO_MEASURES as readonly string[]).includes(measure)) return measure;
  if (measure === RATE_MEASURE) return measure;
  if (units === "cases") return `${measure}${CASES_SUFFIX}`;
  return units === "sticks" ? `${measure}${STICKS_SUFFIX}` : measure;
}

/** Decimals every week-denominated number is stated to — the cover, the target
 *  it is graded against, and the band that colours it. One constant because the
 *  three have to agree: a cover printed to one decimal but BANDED on its raw
 *  value shades an identical "8.0" yellow in one row and green in the next. */
export const COVER_DECIMALS = 1;

/** The value a week-denominated cell actually PRINTS, as a number. Anything
 *  grading such a cell has to judge this rather than the float behind it. */
export function shownWeeks(value: number): number {
  return Number(value.toFixed(COVER_DECIMALS));
}

export type CoverBand = "none" | "stockout" | "short" | "on" | "over";

/** How many x the target reads as overstocked, so the green band does not
 *  swallow a node sitting on years of cover. */
export const OVERSTOCK_MULTIPLE = 3;

/** Which heatmap band a cover falls in. `none` shades nothing — a run that
 *  recorded no target gets no verdict, because grading a plan against a number
 *  this side invented is worse than showing none.
 *
 *  Judged on the PRINTED value, not the raw one. Cover is a forward run-out
 *  whose last term is a division, so a row landing exactly on target comes back
 *  as 7.999999999999999 about as often as 8.0 — one ULP, which is arithmetic
 *  noise rather than a shortfall, and which the reader cannot see because both
 *  print "8.0". Banding the raw value made that invisible difference the whole
 *  verdict: in one published run 712 rows printed "8.0" in yellow beside 1,587
 *  printing "8.0" in green. */
export function coverBand(measure: string, value: number, target: number | null): CoverBand {
  if (!BAND_MEASURES.includes(measure) || !Number.isFinite(value)) return "none";
  const shown = shownWeeks(value);
  if (shown <= 0) return "stockout";
  if (target == null) return "none";
  const goal = shownWeeks(target);
  if (shown < goal) return "short";
  // Rounded like the other two: the raw product carries its own ULP — a target
  // printing 1.1 gives 3 * 1.1 === 3.3000000000000003, so a cover printing
  // "3.3" would read `on`, which is the very defect this function exists to
  // stop. Every comparison here is on the one-decimal scale or none of them is.
  if (shown >= shownWeeks(OVERSTOCK_MULTIPLE * goal)) return "over";
  return "on";
}

/** Coverage gets decimals, quantities are rounded integers. Matched against
 *  RATIO_MEASURES rather than a measure name, which has been renamed before and
 *  silently sent the whole column through the integer branch. */
export function formatMeasureCell(measure: string, value: number): string {
  if (!Number.isFinite(value)) return "—";
  // Weeks, not eaches: the target reads beside the cover it grades, so the two
  // must carry the same decimals or 8.6 renders as 9 against a cover of 8.7.
  if (measure === TARGET_MEASURE) return value.toFixed(COVER_DECIMALS);
  if ((RATIO_MEASURES as readonly string[]).includes(measure)) {
    return value.toFixed(COVER_DECIMALS);
  }
  if (measure === "fill_rate") return `${(value * 100).toFixed(1)}%`;
  // `|| 0` collapses negative zero, which would render as "-0" and read as a
  // shortfall that is not there.
  return (Math.round(value) || 0).toLocaleString("en-US");
}

/** The arrivals term and its two halves, named because the cube reads all three
 *  off one row to decide a cell's lock. */
export const ARRIVALS_MEASURE = "forecasted_transfer_arrivals";
export const BUILD_PLAN_MEASURE = "build_plan_arrivals";
export const RECOMMENDED_MEASURE = "recommended_arrivals";

export type Firmness = "none" | "firm" | "mixed";

/** Half a unit — where `formatMeasureCell` starts rounding to zero, so the lock
 *  cannot contradict the cell it sits in. */
export const FIRMNESS_EPSILON = 0.5;

/** How much of a period's arrivals is committed; `mixed` must not lock, because
 *  the engine is still topping that period up.
 *
 *  The lock is earned against the RESIDUAL, not against the recommended line: the
 *  two halves are projected separately from the term, so a row dropped from the
 *  children alone leaves `recommended` at zero while the term still carries it,
 *  and judging that side would lock supply nobody committed to. */
export function firmness(total: number, firm: number, recommended: number): Firmness {
  if (!Number.isFinite(total) || Math.abs(total) < FIRMNESS_EPSILON) return "none";
  const hasFirm = Number.isFinite(firm) && Math.abs(firm) >= FIRMNESS_EPSILON;
  if (!hasFirm) return "none";
  const residual = total - firm;
  const uncommitted = Math.max(
    Math.abs(residual),
    Number.isFinite(recommended) ? Math.abs(recommended) : 0,
  );
  return uncommitted >= FIRMNESS_EPSILON ? "mixed" : "firm";
}

/** What the lock says on hover, in the measures' own words; a mixed period states
 *  both numbers, since "partly committed" without the split cannot be acted on.
 *  Null where no marker is drawn. */
export function firmnessDetail(
  measure: string,
  total: number,
  firm: number,
  recommended: number,
  committedWord = "Build plan",
): string | null {
  const state = firmness(total, firm, recommended);
  if (state === "none") return null;
  if (state === "firm") return `${committedWord} — committed.`;
  const rest = Number.isFinite(recommended) ? recommended : total - firm;
  return (
    `${committedWord} ${formatMeasureCell(measure, firm)} — committed. ` +
    `Recommended ${formatMeasureCell(measure, rest)}.`
  );
}

/** The cell's cover target, as a string like every other dimension. Per row:
 *  targets are set per stocking-target group, so one run carries several. */
export const COVER_TARGET_COL = "cover_target_weeks";

/** The target row, shown directly beneath the cover it is measured against.
 *  Not a cube measure — a target is a level, so summing it over the rows in a
 *  segment would multiply it. It rides as a dimension and renders flat. */
export const TARGET_MEASURE = "target_weeks_of_supply";

// Columns the cube emits that are NOT period MM/DD/YY cells — the skip set the
// period-column detector checks before the MM/DD/YY regex.
export const NON_PERIOD_COLS: ReadonlySet<string> = new Set([
  ...SEGMENT_OPTIONS.map((o) => o.value),
  "measure",
  // the cube's own period column, which the transform pivots away but which the
  // detector must never mistake for a period cell
  "period",
  // the run's cover target, carried as a grouping column and read off the row
  COVER_TARGET_COL,
]);

/** The unit whose total is only honest within ONE product line, and the column
 *  that tells them apart.
 *
 *  A stick is not a can, so a number that adds them is wrong rather than
 *  imprecise — and wrong quietly, since it renders like any other total. Shared
 *  because both cubes offer that unit, and a rule kept in two places is a rule
 *  that drifts. */
export const BASE_UNIT_SPLIT_COLUMN = "class";

/** The two product lines, exactly as gold's `class` column spells them
 *  (masterdata's top-down workbook constants). Every cube filter and the two
 *  class pages' route scopes read these — a literal kept per-site would drift
 *  from the data silently, and a drifted value empties the page without an
 *  error. */
export const SPARKLING_CLASS = "Sparkling";
export const DRINK_MIX_CLASS = "Drink Mix";

/** The stages a class page's Balance Walk offers. Drink Mix builds its finished
 *  goods where it fills them (no repack site), so it has the one walk. */
export const CLASS_WALK_STAGES: Record<string, readonly Stage[] | undefined> = {
  [SPARKLING_CLASS]: [FINISHED_GOOD_STAGE, FIRST_PACK_STAGE],
  [DRINK_MIX_CLASS]: [FINISHED_GOOD_STAGE],
};

/** A class page's grouping and sort for one walk (`stage`), where they differ from the walk's own. */
export const CLASS_OPENING_VIEW: Record<string, { stage: Stage; segments: string[]; sortBy: string }> = {
  [SPARKLING_CLASS]: {
    stage: FINISHED_GOOD_STAGE,
    segments: ["item_key", "sku_name"],
    sortBy: "ending_weeks_of_supply",
  },
  [DRINK_MIX_CLASS]: {
    stage: FINISHED_GOOD_STAGE,
    segments: ["item_key", "sku_name"],
    sortBy: "ending_weeks_of_supply",
  },
};

/** The grouping `stage` opens on, on a page scoped to `lockedClass`. */
export function defaultSegmentsAt(stage: Stage, lockedClass?: string): string[] {
  const view = lockedClass ? CLASS_OPENING_VIEW[lockedClass] : undefined;
  return view && view.stage === stage ? view.segments : WALK_SPEC[stage].defaultSegments;
}

/** The measure `stage` opens on, on a page scoped to `lockedClass`. */
export function defaultSortAt(stage: Stage, lockedClass?: string): string {
  const view = lockedClass ? CLASS_OPENING_VIEW[lockedClass] : undefined;
  return view && view.stage === stage ? view.sortBy : DEFAULT_MEASURE;
}

/** The measure after a flip: one still at the old walk's default takes the new walk's,
 *  as the grouping does; one the viewer chose maps as {@link sortByForStage} does. */
export function sortAfterFlip(sortBy: string, from: Stage, to: Stage, lockedClass?: string): string {
  return sortBy === defaultSortAt(from, lockedClass) ? defaultSortAt(to, lockedClass) : sortByForStage(sortBy, to);
}

/** Whether a set of rows spans more than one product line — in which case a
 *  number summed across them is adding two units together. */
export function spansProductLines(lines: readonly string[]): boolean {
  return new Set(lines).size > 1;
}

// ── The walk's view params ──────────────────────────────────────────────────
// Here rather than in the cube so the page's engine declaration (enginePages.ts)
// reads the same defaults and choices the walk does, without loading the walk.

export const WALK_PERIOD_OPTIONS: { value: PeriodUnit; label: string }[] = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];

// Kept module-level + stable so useViewParams compares by reference.
export const WALK_VIEW_PARAMS = {
  segments: DEFAULT_SEGMENTS,
  filters: [] as FilterEntry[],
  sortBy: DEFAULT_MEASURE as string,
  units: "eaches" as Units,
  unallocated: "show" as UnallocatedMode,
  period: "week" as PeriodUnit,
  fromKey: "" as string,
  toKey: "" as string,
};
// The page that offers both walks (`walkStages`) carries the production stage
// beside them. A walk-level setting, not a segment: it rides the URL and pins
// like the rest, and the read carries it. The original page never has the key,
// so its links, pins and reads are exactly what they always were.
export const STAGED_WALK_VIEW_PARAMS = { ...WALK_VIEW_PARAMS, stage: DEFAULT_STAGE as string };
/** The staged page's defaults: each walk opens on its own (or its class page's) grouping. */
export const stagedWalkViewParamsFor = (stage: string | null, stages: readonly Stage[], lockedClass?: string) => {
  const opening = openingStage(stage, stages);
  return {
    ...STAGED_WALK_VIEW_PARAMS,
    stage: openingStage(null, stages) as string,
    segments: defaultSegmentsAt(opening, lockedClass),
    sortBy: defaultSortAt(opening, lockedClass),
  };
};
